/**
 * PwaInstallBannerIsland — banner flutuante "Instalar App".
 *
 * Substitui o `#pwa-install-banner` V1 (delivery_app.html:2191) + funções
 * `installPWA / dismissInstall / detectIosBrowser` (delivery_app.html:
 * 12740-12912).
 *
 * Fluxo:
 *  - Android/PC: navegador dispara `beforeinstallprompt` → guardamos o
 *    evento, mostramos banner; click "Instalar" chama `prompt()` nativo.
 *  - iOS Safari real: mostra banner com botão que abre modal explicando
 *    o passo a passo (⎋ Compartilhar → "Adicionar à Tela de Início").
 *  - iOS Chrome/Firefox/Instagram/WhatsApp/etc: Apple BLOQUEIA install
 *    fora do Safari nativo — mostra modal pedindo pra abrir no Safari.
 *  - Dismiss persiste 24h via localStorage `pwa_dismissed_ts`.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { t, useI18n } from '../i18n';

interface StoreMeta {
  logoUrl: string;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type IosBrowser =
  | 'safari'
  | 'chrome-ios'
  | 'firefox-ios'
  | 'edge-ios'
  | 'instagram'
  | 'facebook'
  | 'twitter'
  | 'linkedin'
  | 'tiktok'
  | 'whatsapp'
  | 'telegram'
  | 'pinterest'
  | 'snapchat';

const IOS_BROWSER_LABELS: Record<Exclude<IosBrowser, 'safari'>, string> = {
  'chrome-ios': 'Chrome',
  'firefox-ios': 'Firefox',
  'edge-ios': 'Edge',
  instagram: 'Instagram',
  facebook: 'Facebook',
  twitter: 'X (Twitter)',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  pinterest: 'Pinterest',
  snapchat: 'Snapchat',
};

const DISMISS_KEY = 'pwa_dismissed_ts';
const DISMISS_WINDOW_MS = 24 * 60 * 60 * 1000;

function detectIosBrowser(): IosBrowser {
  const ua = navigator.userAgent;
  if (/CriOS/i.test(ua)) return 'chrome-ios';
  if (/FxiOS/i.test(ua)) return 'firefox-ios';
  if (/EdgiOS/i.test(ua)) return 'edge-ios';
  if (/Instagram/i.test(ua)) return 'instagram';
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return 'facebook';
  if (/Twitter/i.test(ua)) return 'twitter';
  if (/LinkedInApp/i.test(ua)) return 'linkedin';
  if (/Bytedance|musical_ly|TikTok/i.test(ua)) return 'tiktok';
  if (/WhatsApp/i.test(ua)) return 'whatsapp';
  if (/Telegram/i.test(ua)) return 'telegram';
  if (/Pinterest/i.test(ua)) return 'pinterest';
  if (/Snapchat/i.test(ua)) return 'snapchat';
  return 'safari';
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isInStandalone(): boolean {
  return (navigator as Navigator & { standalone?: boolean }).standalone === true
    || window.matchMedia('(display-mode: standalone)').matches;
}

function shouldShowBanner(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return true;
  const ts = Number(raw);
  if (!Number.isFinite(ts)) return true;
  return Date.now() - ts > DISMISS_WINDOW_MS;
}

export function PwaInstallBannerIsland({ logoUrl }: StoreMeta) {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [helperOpen, setHelperOpen] = useState<null | { mode: 'ios-safari' | 'ios-other'; browser?: IosBrowser }>(
    null,
  );

  useEffect(() => {
    if (isInStandalone()) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      if (shouldShowBanner()) setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    if (isIos() && detectIosBrowser() === 'safari' && shouldShowBanner()) {
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* quota */ }
  };

  const install = async () => {
    if (isIos()) {
      const b = detectIosBrowser();
      setHelperOpen(b === 'safari' ? { mode: 'ios-safari' } : { mode: 'ios-other', browser: b });
      return;
    }
    if (!deferred) {
      setHelperOpen({ mode: 'ios-safari' }); // genérico — toque no menu
      return;
    }
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch { /* */ }
    setDeferred(null);
    dismiss();
  };

  if (!visible && !helperOpen) return null;

  return createPortal(
    <>
      {visible && (
        <div className="fixed bottom-4 left-4 right-4 z-[100001] transition-transform duration-500 max-w-[472px] mx-auto">
          <div className="bg-gray-900 text-white p-4 rounded-2xl shadow-2xl border border-gray-700 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="bg-white/10 p-2 rounded-xl shrink-0">
                <img
                  src={logoUrl}
                  alt=""
                  className="w-10 h-10 object-contain"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://placehold.co/100?text=App'; }}
                />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-sm">{t('Instalar App')}</p>
                <p className="text-xs text-gray-400">{t('Adicione à tela inicial')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={dismiss}
                aria-label={t("Fechar")}
                className="text-gray-400 p-2 hover:text-white"
              >
                <i className="fas fa-times" />
              </button>
              <button
                type="button"
                onClick={install}
                className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2 px-4 rounded-xl shadow-lg"
              >
                {t('Instalar')}
              </button>
            </div>
          </div>
        </div>
      )}
      {helperOpen && (
        <div className="fixed inset-0 z-[100002] flex items-end sm:items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setHelperOpen(null)}
          />
          <div className="relative bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <button
              type="button"
              onClick={() => setHelperOpen(null)}
              aria-label={t("Fechar")}
              className="absolute top-3 right-3 w-9 h-9 rounded-full text-gray-500 hover:bg-gray-100 flex items-center justify-center"
            >
              <i className="fas fa-times" />
            </button>
            {helperOpen.mode === 'ios-safari' ? (
              <>
                <h3 className="text-lg font-extrabold text-gray-900 mb-2">{t('Instalar no iPhone')}</h3>
                <p className="text-sm text-gray-600 mb-3">{t('É bem rapidinho:')}</p>
                <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
                  <li>{t('Toque em')} <span className="text-blue-600 text-base">⎋</span> (botão <b>{t('Compartilhar')}</b> na barra de baixo)</li>
                  <li>{t('Role a lista e toque em')} <b>{t('"Adicionar à Tela de Início"')}</b></li>
                  <li>{t('Confirme tocando em')} <b>{t('Adicionar')}</b>{t(', no canto superior direito')}</li>
                </ol>
                <p className="text-xs text-gray-500 mt-3">
                  {t('O app vai aparecer na sua tela inicial como qualquer outro aplicativo.')}
                </p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-extrabold text-gray-900 mb-2">{t('Abra no Safari pra instalar')}</h3>
                <p className="text-sm text-gray-700">
                  {t('Você está usando o')} <b>
                    {helperOpen.browser && helperOpen.browser !== 'safari'
                      ? IOS_BROWSER_LABELS[helperOpen.browser]
                      : 'navegador atual'}
                  </b>{t('. A Apple só permite instalar pelo')} <b>{t('Safari')}</b>.
                </p>
                <p className="text-sm text-gray-700 font-bold mt-3">{t('Como abrir no Safari:')}</p>
                <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2 mt-1">
                  <li>{t('Toque nos')} <b>{t('três pontinhos')}</b> (•••) ou em <b>{t('"Abrir em..."')}</b>.</li>
                  <li>{t('Escolha')} <b>{t('Safari')}</b>.</li>
                  <li>
                    {t('No Safari, toque em')} <span className="text-blue-600 text-base">⎋</span> (Compartilhar) →{' '}
                    <b>{t('"Adicionar à Tela de Início"')}</b>.
                  </li>
                </ol>
                <p className="text-xs text-gray-500 mt-3">{t('Ou copie e cole o link no Safari:')}</p>
                <input
                  readOnly
                  value={window.location.href}
                  onClick={(e) => { (e.currentTarget as HTMLInputElement).select(); }}
                  className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono text-gray-700 bg-gray-50 cursor-copy"
                />
              </>
            )}
            <button
              type="button"
              onClick={() => setHelperOpen(null)}
              className="mt-5 w-full py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm"
            >
              {t('Entendi')}
            </button>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
